export interface ArticlePdfData {
  title: string;
  description?: string | null;
  content?: string | null;
  category?: string | null;
  type?: 'article' | 'video' | string | null;
  readTime?: string | null;
  updatedLabel?: string | null;
  fileName?: string | null;
}

const slugify = (text?: string | null): string =>
  String(text || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * Builds an A4 PDF for an article including Title, Short Description, and formatted Content,
 * returning the generated Blob and calculated fileName.
 */
export async function generateArticlePdfBlob(
  data: ArticlePdfData,
): Promise<{ blob: Blob; fileName: string }> {
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentW = pageW - margin * 2;
  let y = 25;

  const checkPageBreak = (neededHeight: number) => {
    if (y + neededHeight > pageH - margin) {
      doc.addPage();
      y = margin;
    }
  };

  // 1. Category / Article type tag
  const cat = (data.category || '').trim();
  const isVideo = data.type === 'video';
  const categoryText = cat
    ? `${cat.toUpperCase()} • ${isVideo ? 'VIDEO TUTORIAL' : 'ARTICLE'}`
    : 'HELP ARTICLE';
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(37, 99, 235); // Blue
  doc.text(categoryText, margin, y);
  y += 7;

  // 2. Article Title
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(15, 23, 42); // Slate 900
  const titleText = (data.title || 'Untitled Article').trim();
  const titleLines = doc.splitTextToSize(titleText, contentW);
  checkPageBreak(titleLines.length * 8 + 4);
  doc.text(titleLines, margin, y);
  y += titleLines.length * 8 + 3;

  // 3. Short Description
  if (data.description && data.description.trim()) {
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139); // Slate 500
    const descLines = doc.splitTextToSize(data.description.trim(), contentW);
    checkPageBreak(descLines.length * 5.5 + 4);
    doc.text(descLines, margin, y);
    y += descLines.length * 5.5 + 4;
  }

  // 4. Meta row (Read time / Updated date)
  const metaParts: string[] = [];
  if (data.readTime && data.readTime.trim()) metaParts.push(data.readTime.trim());
  if (data.updatedLabel && data.updatedLabel.trim()) metaParts.push(data.updatedLabel.trim());
  if (metaParts.length > 0) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(148, 163, 184); // Slate 400
    checkPageBreak(6);
    doc.text(metaParts.join(' • '), margin, y);
    y += 6;
  }

  // Divider line
  checkPageBreak(6);
  doc.setDrawColor(226, 232, 240); // Slate 200
  doc.setLineWidth(0.4);
  doc.line(margin, y, pageW - margin, y);
  y += 8;

  // 5. Article Content
  if (data.content && data.content.trim()) {
    if (typeof DOMParser !== 'undefined') {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(data.content, 'text/html');
      const bodyElements = Array.from(parsedDoc.body.children);

      if (bodyElements.length === 0) {
        const text = parsedDoc.body.textContent || '';
        const paragraphs = text.split('\n').map((p) => p.trim()).filter(Boolean);
        doc.setFontSize(10.5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(51, 65, 85);
        for (const p of paragraphs) {
          const lines = doc.splitTextToSize(p, contentW);
          checkPageBreak(lines.length * 5 + 4);
          doc.text(lines, margin, y);
          y += lines.length * 5 + 4;
        }
      } else {
        for (const el of bodyElements) {
          const tag = el.tagName.toLowerCase();
          const text = (el.textContent || '').trim();

          // Image support
          if (tag === 'img') {
            const imgSrc = (el as HTMLImageElement).getAttribute('src') || '';
            if (typeof document !== 'undefined') {
              const domImg = document.querySelector<HTMLImageElement>(`img[src="${imgSrc}"]`);
              if (domImg && domImg.complete && domImg.naturalWidth > 0) {
                try {
                  const aspect = domImg.naturalHeight / domImg.naturalWidth;
                  const imgW = Math.min(contentW, 130);
                  const imgH = imgW * aspect;
                  checkPageBreak(imgH + 4);
                  doc.addImage(domImg, 'PNG', margin, y, imgW, imgH);
                  y += imgH + 4;
                } catch {
                  // Ignore CORS security issues
                }
              }
            }
            continue;
          }

          if (!text) continue;

          if (tag === 'h1' || tag === 'h2') {
            checkPageBreak(12);
            doc.setFontSize(14);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(15, 23, 42);
            const lines = doc.splitTextToSize(text, contentW);
            doc.text(lines, margin, y);
            y += lines.length * 6 + 4;
          } else if (tag === 'h3' || tag === 'h4') {
            checkPageBreak(10);
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(30, 41, 59);
            const lines = doc.splitTextToSize(text, contentW);
            doc.text(lines, margin, y);
            y += lines.length * 5.5 + 3;
          } else if (tag === 'ul' || tag === 'ol') {
            const listItems = Array.from(el.querySelectorAll('li'));
            let itemIdx = 1;
            for (const li of listItems) {
              const liText = (li.textContent || '').trim();
              if (!liText) continue;
              const prefix = tag === 'ol' ? `${itemIdx++}. ` : '• ';
              doc.setFontSize(10);
              doc.setFont('helvetica', 'normal');
              doc.setTextColor(51, 65, 85);
              const indent = 5;
              const bulletW = doc.getTextWidth(prefix);
              const lines = doc.splitTextToSize(liText, contentW - indent - bulletW);
              checkPageBreak(lines.length * 5 + 2);
              doc.text(prefix, margin + indent, y);
              doc.text(lines, margin + indent + bulletW, y);
              y += lines.length * 5 + 2.5;
            }
            y += 2;
          } else if (tag === 'blockquote' || el.classList.contains('bg-blue-50') || el.classList.contains('callout')) {
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(30, 58, 138);
            const calloutIndent = 6;
            const lines = doc.splitTextToSize(text, contentW - calloutIndent * 2);
            const boxH = lines.length * 5 + 6;
            checkPageBreak(boxH + 4);

            doc.setFillColor(239, 246, 255);
            doc.rect(margin, y - 2, contentW, boxH, 'F');
            doc.setDrawColor(37, 99, 235);
            doc.setLineWidth(1);
            doc.line(margin, y - 2, margin, y - 2 + boxH);

            doc.text(lines, margin + calloutIndent, y + 3);
            y += boxH + 4;
          } else {
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(51, 65, 85);
            const lines = doc.splitTextToSize(text, contentW);
            checkPageBreak(lines.length * 5 + 3.5);
            doc.text(lines, margin, y);
            y += lines.length * 5 + 3.5;
          }
        }
      }
    } else {
      // Fallback if no DOMParser (plain text)
      const paragraphs = data.content.split('\n').map((p) => p.trim()).filter(Boolean);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(51, 65, 85);
      for (const p of paragraphs) {
        const lines = doc.splitTextToSize(p, contentW);
        checkPageBreak(lines.length * 5 + 3.5);
        doc.text(lines, margin, y);
        y += lines.length * 5 + 3.5;
      }
    }
  }

  // Footer: page numbers
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(148, 163, 184);
    doc.text(`Page ${i} of ${totalPages}`, pageW - margin, pageH - 10, { align: 'right' });
  }

  const baseFileName = data.fileName
    ? (data.fileName.toLowerCase().endsWith('.pdf') ? data.fileName : `${data.fileName}.pdf`)
    : `${slugify(data.title) || 'article'}.pdf`;

  const blob = doc.output('blob');
  return { blob, fileName: baseFileName };
}

/**
 * Generates an A4 PDF for an article including Title, Short Description, and formatted Content,
 * and triggers immediate download in the browser.
 */
export async function generateArticlePdf(data: ArticlePdfData): Promise<void> {
  const { blob, fileName } = await generateArticlePdfBlob(data);
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

/**
 * Resolves a file URL against an optional API base URL if relative.
 */
export function resolveArticleFileUrl(url?: string | null, apiBaseUrl?: string): string {
  const cleanUrl = String(url ?? '').trim();
  if (!cleanUrl || cleanUrl === 'null' || cleanUrl === 'undefined') return '';
  if (/^https?:\/\//i.test(cleanUrl) || cleanUrl.startsWith('blob:') || cleanUrl.startsWith('data:')) {
    return cleanUrl;
  }
  const base = (apiBaseUrl || '').replace(/\/$/, '');
  if (!base) return cleanUrl;
  return cleanUrl.startsWith('/') ? `${base}${cleanUrl}` : `${base}/${cleanUrl}`;
}

/**
 * Downloads an uploaded PDF file from its URL:
 * - Attempts to fetch as blob to preserve the exact file name and trigger local save.
 * - Falls back to an anchor download click if fetch fails (e.g. cross-origin CORS).
 */
export async function downloadArticlePdfFromUrl(
  url: string,
  fileName: string,
  apiBaseUrl?: string,
): Promise<void> {
  const resolvedUrl = resolveArticleFileUrl(url, apiBaseUrl);
  if (!resolvedUrl) return;

  const resolvedName = fileName.trim()
    ? (fileName.toLowerCase().endsWith('.pdf') ? fileName : `${fileName}.pdf`)
    : 'article.pdf';

  try {
    const response = await fetch(resolvedUrl);
    if (response.ok) {
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = resolvedName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
      return;
    }
  } catch (err) {
    console.warn('Native fetch of PDF blob failed, falling back to direct link download:', err);
  }

  // Fallback: direct anchor download
  const link = document.createElement('a');
  link.href = resolvedUrl;
  link.download = resolvedName;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

